import { FC, useEffect, useMemo, useState } from 'react';

import { router } from '@inertiajs/react';
import { ArrowRight, Minus, Plus, RotateCcw, Trash2 } from 'lucide-react';

import { deliveryMethodLabel, formatOrderAmount } from './orderRevisionUtils';
import { OrderServiceFeePreview } from './OrderServiceFeePreview';
import { Button } from '../../../components/ui/Button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '../../../components/ui/Dialog';
import { Image } from '../../../components/ui/Image';
import { profileOrderRevisionRoute } from '../../../models/routes';
import { OrdersService } from '../../../services/OrdersService';
import { OfferProperty, Order, OrderDelivery, OrderItem } from '../../../types';

interface Props {
    order: Order | null;
    offerProperties: OfferProperty[];
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

type DraftItem = {
    order_item_id: number;
    quantity: number;
    removed: boolean;
};

const initialItems = (order: Order | null): DraftItem[] =>
    (order?.order_items || []).map((item) => ({
        order_item_id: item.id,
        quantity: item.amount,
        removed: false,
    }));

export const OrderEditDialog: FC<Props> = ({
    order,
    offerProperties,
    open,
    onOpenChange,
}) => {
    const [items, setItems] = useState<DraftItem[]>(() => initialItems(order));
    const [deliveryMethod, setDeliveryMethod] = useState<
        OrderDelivery['method'] | ''
    >('');
    const [processing, setProcessing] = useState(false);
    const [error, setError] = useState('');

    const currentDeliveryMethod =
        order?.revision_delivery_method || order?.delivery?.method || '';

    useEffect(() => {
        if (!open) return;

        setItems(initialItems(order));
        setDeliveryMethod(currentDeliveryMethod);
        setError('');
    }, [currentDeliveryMethod, open, order]);

    const itemById = useMemo(
        () =>
            new Map<number, OrderItem>(
                (order?.order_items || []).map((item) => [item.id, item]),
            ),
        [order],
    );

    const activeItemCount = items.filter((item) => !item.removed).length;
    const invalidQuantity = items.some((draft) => {
        if (draft.removed) return false;
        const item = itemById.get(draft.order_item_id);
        return (
            draft.quantity < 1 ||
            draft.quantity >
                (item?.maximum_revision_quantity ?? item?.amount ?? 1)
        );
    });
    const itemChanged = items.some((draft) => {
        const item = itemById.get(draft.order_item_id);
        return draft.removed || draft.quantity !== item?.amount;
    });
    const deliveryChanged = deliveryMethod !== currentDeliveryMethod;
    const changed = itemChanged || deliveryChanged;

    const newTotal = useMemo(() => {
        if (!order) return 0;

        return items.reduce((total, draft) => {
            const item = itemById.get(draft.order_item_id);
            if (!item) return total;
            const price = item.price_per_item;
            const newQuantity = draft.removed ? 0 : draft.quantity;
            return total + price * (newQuantity - item.amount);
        }, order.total_price || 0);
    }, [itemById, items, order]);

    if (!order) return null;

    const updateItem = (orderItemId: number, patch: Partial<DraftItem>) => {
        setItems((current) =>
            current.map((item) =>
                item.order_item_id === orderItemId
                    ? { ...item, ...patch }
                    : item,
            ),
        );
        setError('');
    };

    const submit = () => {
        if (activeItemCount === 0) {
            setError('Не можна видалити всі товари із замовлення.');
            return;
        }
        if (invalidQuantity) {
            setError('Перевірте доступну кількість товарів.');
            return;
        }

        setProcessing(true);
        router.post(
            profileOrderRevisionRoute(order.id),
            {
                revision: {
                    delivery_method: deliveryMethod,
                    items,
                },
            },
            {
                preserveScroll: true,
                onError: (errors) =>
                    setError(
                        String(
                            errors.general || 'Не вдалося запропонувати зміни.',
                        ),
                    ),
                onSuccess: () => onOpenChange(false),
                onFinish: () => setProcessing(false),
            },
        );
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="h-[100dvh] w-full max-w-none gap-0 overflow-hidden rounded-none p-0 sm:h-auto sm:max-h-[90vh] sm:w-[calc(100%-2rem)] sm:max-w-3xl sm:rounded-2xl">
                <DialogHeader className="border-b px-4 py-5 pr-12 text-left sm:px-6">
                    <DialogTitle>
                        Редагування замовлення #{order.number}
                    </DialogTitle>
                    <DialogDescription>
                        Після збереження покупець повинен підтвердити
                        запропоновані зміни.
                    </DialogDescription>
                </DialogHeader>

                <div className="min-h-0 flex-1 space-y-6 overflow-y-auto px-4 py-5 sm:px-6">
                    <section>
                        <h3 className="mb-3 text-sm font-semibold">Товари</h3>
                        <div className="space-y-3">
                            {items.map((draft) => {
                                const item = itemById.get(draft.order_item_id);
                                if (!item) return null;
                                const maximum =
                                    item.maximum_revision_quantity ??
                                    item.amount;
                                const properties =
                                    OrdersService.getOfferPropertyEntries(
                                        item.offer?.property_set,
                                        offerProperties,
                                    );

                                return (
                                    <div
                                        key={item.id}
                                        className={`rounded-xl border p-3 ${draft.removed ? 'border-destructive/40 bg-destructive/5' : 'bg-card'}`}
                                    >
                                        <div className="flex gap-3">
                                            <div className="h-20 w-14 flex-shrink-0 overflow-hidden rounded-md bg-muted">
                                                <Image
                                                    src={
                                                        item.product?.image ||
                                                        '/placeholder.svg'
                                                    }
                                                    alt={
                                                        item.product
                                                            ?.image_alt ||
                                                        item.product?.name
                                                    }
                                                    className="object-cover"
                                                />
                                            </div>
                                            <div className="min-w-0 flex-1">
                                                <div className="flex items-start justify-between gap-2">
                                                    <div className="min-w-0">
                                                        <p className="font-medium">
                                                            {item.product?.name}
                                                        </p>
                                                        {properties.length >
                                                            0 && (
                                                            <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">
                                                                {properties
                                                                    .map(
                                                                        (
                                                                            property,
                                                                        ) =>
                                                                            `${property.key}: ${property.value}`,
                                                                    )
                                                                    .join(', ')}
                                                            </p>
                                                        )}
                                                        <p className="mt-1 text-sm text-muted-foreground">
                                                            {formatOrderAmount(
                                                                item.price_per_item,
                                                            )}{' '}
                                                            / шт.
                                                        </p>
                                                    </div>
                                                    <Button
                                                        type="button"
                                                        size="icon"
                                                        variant="ghost"
                                                        className="h-8 w-8 flex-shrink-0 text-destructive"
                                                        aria-label={
                                                            draft.removed
                                                                ? 'Повернути товар'
                                                                : 'Видалити товар'
                                                        }
                                                        onClick={() =>
                                                            updateItem(
                                                                item.id,
                                                                {
                                                                    removed:
                                                                        !draft.removed,
                                                                },
                                                            )
                                                        }
                                                    >
                                                        {draft.removed ? (
                                                            <RotateCcw className="h-4 w-4" />
                                                        ) : (
                                                            <Trash2 className="h-4 w-4" />
                                                        )}
                                                    </Button>
                                                </div>

                                                {draft.removed ? (
                                                    <p className="mt-3 text-sm font-semibold text-destructive">
                                                        Видалено із замовлення
                                                    </p>
                                                ) : (
                                                    <div className="mt-3 flex flex-wrap items-start justify-between gap-3">
                                                        <div>
                                                            <div className="flex items-center rounded-xl border">
                                                                <Button
                                                                    type="button"
                                                                    size="icon"
                                                                    variant="ghost"
                                                                    className="h-9 w-9 rounded-r-none"
                                                                    aria-label="Зменшити кількість"
                                                                    disabled={
                                                                        draft.quantity <=
                                                                        1
                                                                    }
                                                                    onClick={() =>
                                                                        updateItem(
                                                                            item.id,
                                                                            {
                                                                                quantity:
                                                                                    draft.quantity -
                                                                                    1,
                                                                            },
                                                                        )
                                                                    }
                                                                >
                                                                    <Minus className="h-4 w-4" />
                                                                </Button>
                                                                <span className="w-10 text-center text-sm font-semibold tabular-nums">
                                                                    {
                                                                        draft.quantity
                                                                    }
                                                                </span>
                                                                <Button
                                                                    type="button"
                                                                    size="icon"
                                                                    variant="ghost"
                                                                    className="h-9 w-9 rounded-l-none"
                                                                    aria-label="Збільшити кількість"
                                                                    disabled={
                                                                        draft.quantity >=
                                                                        maximum
                                                                    }
                                                                    onClick={() =>
                                                                        updateItem(
                                                                            item.id,
                                                                            {
                                                                                quantity:
                                                                                    draft.quantity +
                                                                                    1,
                                                                            },
                                                                        )
                                                                    }
                                                                >
                                                                    <Plus className="h-4 w-4" />
                                                                </Button>
                                                            </div>
                                                            <p className="mt-1 text-xs text-muted-foreground">
                                                                {draft.quantity >=
                                                                maximum
                                                                    ? 'Більше немає в наявності.'
                                                                    : `Можна вказати до ${maximum} шт.`}
                                                            </p>
                                                        </div>
                                                        <div className="text-right">
                                                            <p className="text-xs text-muted-foreground">
                                                                Разом
                                                            </p>
                                                            <p className="font-semibold">
                                                                {formatOrderAmount(
                                                                    item.price_per_item *
                                                                        draft.quantity,
                                                                )}
                                                            </p>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </section>

                    <section>
                        <h3 className="mb-3 text-sm font-semibold">
                            Спосіб доставки
                        </h3>
                        <div className="grid gap-2 sm:grid-cols-3">
                            {(
                                order.available_revision_delivery_methods || []
                            ).map((method) => (
                                <label
                                    key={method}
                                    className={`cursor-pointer rounded-xl border p-3 text-sm transition-colors ${deliveryMethod === method ? 'border-primary bg-primary/5 font-semibold' : 'hover:bg-muted/40'}`}
                                >
                                    <input
                                        type="radio"
                                        name="revision_delivery_method"
                                        value={method}
                                        checked={deliveryMethod === method}
                                        onChange={() =>
                                            setDeliveryMethod(method)
                                        }
                                        className="mr-2"
                                    />
                                    {deliveryMethodLabel(method)}
                                </label>
                            ))}
                        </div>
                    </section>
                </div>

                <div className="sticky bottom-0 border-t bg-background px-4 py-4 shadow-[0_-8px_24px_-18px_rgba(0,0,0,0.45)] sm:px-6">
                    {activeItemCount === 0 && (
                        <p className="mb-3 text-sm font-medium text-destructive">
                            Не можна видалити всі товари із замовлення.
                        </p>
                    )}
                    {error && (
                        <p className="mb-3 text-sm font-medium text-destructive">
                            {error}
                        </p>
                    )}
                    <div className="mb-3 grid gap-2 text-sm sm:grid-cols-2">
                        <div className="flex items-center justify-between gap-3">
                            <span className="text-muted-foreground">
                                Поточна сума:
                            </span>
                            <span className="font-semibold">
                                {formatOrderAmount(order.total_price || 0)}
                            </span>
                        </div>
                        <div className="flex items-center justify-between gap-3">
                            <span className="text-muted-foreground">
                                Нова сума:
                            </span>
                            <span
                                className={`font-semibold ${newTotal !== order.total_price ? 'text-primary' : ''}`}
                            >
                                {formatOrderAmount(newTotal)}
                            </span>
                        </div>
                    </div>
                    <OrderServiceFeePreview
                        applicable={Boolean(order.service_fee_applicable)}
                        currentFeeCents={order.service_fee_cents || 0}
                        newTotal={newTotal}
                    />
                    {deliveryChanged && (
                        <div className="mb-3 flex flex-wrap items-center gap-2 text-sm">
                            <span className="text-muted-foreground">
                                Доставка:
                            </span>
                            <span className="font-medium">
                                {deliveryMethodLabel(currentDeliveryMethod)}
                            </span>
                            <ArrowRight className="h-4 w-4 text-muted-foreground" />
                            <span className="font-semibold text-primary">
                                {deliveryMethodLabel(deliveryMethod)}
                            </span>
                        </div>
                    )}
                    <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                        <Button
                            type="button"
                            variant="outline"
                            disabled={processing}
                            onClick={() => onOpenChange(false)}
                        >
                            Скасувати
                        </Button>
                        <Button
                            type="button"
                            disabled={
                                processing ||
                                !changed ||
                                invalidQuantity ||
                                activeItemCount === 0
                            }
                            onClick={submit}
                        >
                            {processing ? 'Надсилання…' : 'Запропонувати зміни'}
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};
